import { weenatIntl as wintl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import { Resource } from '../Resource';
import { apiVersion } from '../apiVersion';
import { isOptionWithoutUsageOrDescription } from '../core/options';
import { isRawPlotOptionActivation } from '../core/utils/resourcesTypeCheckers';
import * as methods from '../methods';
import * as transformers from '../transformers';
import { OptionCode } from './../enums';
import boundingBoxes from './boundingBoxes';
import measurements from './measurements';
import myFarm from './myFarm';
import orgs from './orgs';
import plotOptionActivations, { fromRaw as fromRawPlotOptionActivation } from './plotOptionActivations';
import plots from './plots';
import stationOptionActivations, { fromRaw as fromRawStationOptionActivation } from './stationOptionActivations';
import stations from './stations';
const rootPath = 'options/catalogue';
/**
 * Entity on which the option can apply
 */
export var ApplicableTo;
(function (ApplicableTo) {
    ApplicableTo[ApplicableTo["organization"] = 0] = "organization";
    ApplicableTo[ApplicableTo["plot"] = 1] = "plot";
    ApplicableTo[ApplicableTo["user"] = 2] = "user";
    ApplicableTo[ApplicableTo["network"] = 3] = "network";
})(ApplicableTo || (ApplicableTo = {}));
/**
 * Different releases status of options
 */
export var ReleaseStatus;
(function (ReleaseStatus) {
    ReleaseStatus[ReleaseStatus["public"] = 0] = "public";
    ReleaseStatus[ReleaseStatus["beta"] = 1] = "beta";
    ReleaseStatus[ReleaseStatus["private"] = 2] = "private";
})(ReleaseStatus || (ReleaseStatus = {}));
const getUnusedApprovalRequestCount = (approvalsCount, organizationOptionActivationsCount) => {
    // Activation from an advizor account bypasses the approval process so we might end up with a negative number of unusedApprovalRequest
    const unusedOptionActivationApprovalRequestsCount = (isNil(approvalsCount) ? 0 : approvalsCount) -
        (isNil(organizationOptionActivationsCount) ? 0 : organizationOptionActivationsCount);
    return unusedOptionActivationApprovalRequestsCount < 0
        ? 0
        : unusedOptionActivationApprovalRequestsCount;
};
const fromRaw = {
    get: ({ applicable_to, approvals_count, chargeable, code, created_at, current_activations_count, id, params, price, release_status, target_plan, remaining_tokens_count, plot, station }) => ({
        name: wintl.t(`models.option.names.${code}`, { capitalize: true }),
        usage: !isOptionWithoutUsageOrDescription(code)
            ? wintl.t(`models.option.usages.${code}`)
            : undefined,
        applicableTo: applicable_to,
        approvalsCount: approvals_count,
        code,
        billingStatusAvailability: target_plan,
        createdAt: created_at,
        id,
        isChargeable: chargeable,
        mandatoryMetricsIds: !isNil(params) ? params.mandatory_metrics ?? [] : [],
        organizationOptionActivationsCount: current_activations_count,
        numberOfGrantedPlotActivationsPerPurchase: !isNil(params) && !isNil(params.tokens) ? params.tokens : null,
        numberOfGrantedSmsPerPurchase: code === OptionCode.sms100 && !isNil(params) && !isNil(params.tokens) ? params.tokens : null,
        price,
        releaseStatus: release_status,
        remainingTokens: remaining_tokens_count,
        reseller: !isNil(params) && !isNil(params.reseller) ? params.reseller : null,
        allowVirtualDevices: !isNil(params) && !isNil(params.allow_virtual_devices) ? params.allow_virtual_devices : false,
        unusedOptionActivationApprovalRequestsCount: getUnusedApprovalRequestCount(approvals_count, current_activations_count),
        plotId: plot,
        stationId: station
    }),
    activate: (raw) => {
        if (isRawPlotOptionActivation(raw)) {
            return fromRawPlotOptionActivation.get(raw);
        }
        else {
            return fromRawStationOptionActivation.get(raw);
        }
    }
};
const toRaw = {
    activate: ({ optionSettings, plotId, stationId, organizationId }) => ({
        plot: plotId,
        organization: organizationId,
        station: stationId,
        settings: optionSettings
    }),
    activationRequest: ({ optionCode, applicableTo, organizationId, releaseStatus }) => ({
        option: optionCode,
        applicable_to: applicableTo,
        organization: organizationId,
        release_status: releaseStatus
    })
};
const cacheConfig = {
    staleTime: 60 * 60 * 1000
};
const options = Resource((requesterBuilder, config) => {
    const getMany = (query) => requesterBuilder({
        path: rootPath,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw.get),
            params: {
                query: {
                    target_plan: query?.billingStatusAvailability,
                    applicable_to: query?.applicableTo,
                    release_status: query?.releaseStatus,
                    organization: query?.organizationId,
                    limit: query?.limit,
                    offset: query?.offset
                }
            }
        },
        config: cacheConfig
    });
    const get = (code, query) => requesterBuilder({
        path: `${rootPath}/${code}`,
        requestCtx: {
            method: 'GET',
            transform: fromRaw.get,
            params: {
                query: {
                    organization: query.organizationId
                }
            }
        },
        config: cacheConfig
    });
    return {
        activate: (code, invalidationConfig) => methods.create({
            rootPath: `${rootPath}/${code}/activate`,
            requesterBuilder,
            fromRaw: fromRaw.activate,
            toRaw: toRaw.activate,
            buildKeysToInvalidate: () => {
                const keys = [
                    // might update the current option
                    get(code, { organizationId: invalidationConfig.organizationId }).invalidationKey,
                    // might update the targeted org
                    orgs(requesterBuilder, config).get(invalidationConfig.organizationId).invalidationKey,
                    orgs(requesterBuilder, config).getMany().invalidationKey,
                    // will update the resource
                    plots(requesterBuilder, config).get(invalidationConfig.plotId).invalidationKey,
                    stations(requesterBuilder, config).get(invalidationConfig.stationId).invalidationKey,
                    // might update the following lists if a station is transformed into a plot
                    plots(requesterBuilder, config).getMany().invalidationKey,
                    stations(requesterBuilder, config).getMany().invalidationKey,
                    myFarm(requesterBuilder, config).getMany().invalidationKey,
                    measurements(requesterBuilder, config).plots.summaries.getAllPages().invalidationKey,
                    measurements(requesterBuilder, config).plotsAndStations.summaries.getAllPages()
                        .invalidationKey,
                    // if irrelis is activated the universes will also change
                    measurements(requesterBuilder, config).plotsAndStations.summaries.getAvailableUniverses({
                        organizationId: invalidationConfig.organizationId
                    }).invalidationKey,
                    measurements(requesterBuilder, config).plots.summaries.getAvailableUniverses({
                        organizationId: invalidationConfig.organizationId
                    }).invalidationKey,
                    // will update the lists of activations
                    plotOptionActivations(requesterBuilder, config).getMany({
                        organizationId: invalidationConfig.organizationId,
                        plotIds: !isNil(invalidationConfig.plotId) ? [invalidationConfig.plotId] : undefined
                    }).invalidationKey,
                    stationOptionActivations(requesterBuilder, config).getMany({
                        organizationId: invalidationConfig.organizationId,
                        stationIds: !isNil(invalidationConfig.stationId)
                            ? [invalidationConfig.stationId]
                            : undefined
                    }).invalidationKey,
                    boundingBoxes(requesterBuilder, config).plots.getBoundingBox({
                        organizationId: invalidationConfig.organizationId
                    }).invalidationKey,
                    boundingBoxes(requesterBuilder, config).plotsAndStations.getBoundingBox({
                        organizationId: invalidationConfig.organizationId
                    }).invalidationKey
                ];
                return keys;
            },
            ...apiVersion[1.1]
        })(),
        catalogue: methods.getAllPages({
            getMany,
            cacheConfig
        }),
        get,
        getMany,
        getPage: methods.getPage({
            getMany
        }),
        requestActivation: methods.create({
            requesterBuilder,
            rootPath: `${rootPath}/request_activation`,
            fromRaw: () => undefined,
            parse: async (res) => res,
            toRaw: toRaw.activationRequest
        })
    };
});
export default options;
